// src/pages/programs/ProgramsList.jsx
import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PlusIcon, ClipboardDocumentListIcon, UserGroupIcon, MagnifyingGlassIcon, RectangleGroupIcon } from '@heroicons/react/24/outline';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import useClubPrograms from '@/hooks/useClubPrograms';
import { formatCents } from '@/utils/programPricing';
import { format } from 'date-fns';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// ── helpers ────────────────────────────────────────────────────────────────────
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}

const STATUS_BADGES = {
  draft:    { label: 'Draft',    cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  open:     { label: 'Open',     cls: 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/40' },
  closed:   { label: 'Closed',   cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  archived: { label: 'Archived', cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

// ── component ──────────────────────────────────────────────────────────────────
export default function ProgramsList() {
  const { clubId } = useParams();
  const { programs, loading } = useClubPrograms(clubId);
  const [regCounts, setRegCounts] = useState({}); // { [programId]: number }
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch active registration counts for each program. Must include
  // clubId — registrations rule gates on isClubStaff(resource.data.clubId)
  // for program regs, and Firestore can't pattern-match the rule unless
  // the query also constrains clubId.
  useEffect(() => {
    if (!clubId || !programs.length) return;
    programs.forEach(async (p) => {
      try {
        const snap = await getCountFromServer(
          query(
            collection(db, 'registrations'),
            where('clubId',    '==', clubId),
            where('programId', '==', p.id),
          )
        );
        setRegCounts((prev) => ({ ...prev, [p.id]: snap.data().count }));
      } catch (e) {
        if (import.meta.env.DEV) console.warn('[ProgramsList] reg count failed', p.id, e);
      }
    });
  }, [clubId, programs]);

  const filteredPrograms = programs.filter((p) => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(q) ||
        p.sport?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const STATUS_TABS = [
    { key: 'all',      label: 'All' },
    { key: 'open',     label: 'Open' },
    { key: 'draft',    label: 'Draft' },
    { key: 'closed',   label: 'Closed' },
    { key: 'archived', label: 'Archived' },
  ];

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]" style={{ fontFamily: HEAD_FONT }}>
              Club
            </p>
            <h1 className="mt-1 text-2xl font-bold uppercase tracking-tight text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
              Programs
            </h1>
            <p className="text-sm text-zinc-500 mt-1">Club-wide registration programs across all teams</p>
          </div>
          <Link
            to={`/clubs/${clubId}/programs/create`}
            className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
            style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
          >
            <PlusIcon className="h-4 w-4" />
            New Program
          </Link>
        </div>

        {/* Search + status filter */}
        {!loading && programs.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search programs…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 rounded-xl border border-zinc-200 bg-white text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 w-56"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_TABS.map((tab) => {
                const count = tab.key === 'all' ? programs.length : programs.filter(p => p.status === tab.key).length;
                if (tab.key !== 'all' && count === 0) return null;
                const active = filterStatus === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilterStatus(tab.key)}
                    className={`px-3 py-1.5 rounded-xl border text-xs font-bold uppercase transition-all ${
                      active
                        ? 'bg-[#0d1f14] text-white border-[#0d1f14]'
                        : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]/40'
                    }`}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
                  >
                    {tab.label}
                    <span className={`ml-1.5 text-[10px] ${active ? 'text-white/70' : 'text-zinc-400'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-20 text-zinc-400 text-sm">Loading programs…</div>
        )}

        {/* Empty state */}
        {!loading && programs.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-zinc-200 bg-white py-20 text-center px-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 mb-4">
              <ClipboardDocumentListIcon className="h-7 w-7 text-[#5a8a1f]" />
            </div>
            <h2 className="text-lg font-bold uppercase text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
              No programs yet
            </h2>
            <p className="text-sm text-zinc-500 mt-1 mb-6 max-w-sm">
              Create a program to open club-wide registration across multiple teams and divisions.
            </p>
            <Link
              to={`/clubs/${clubId}/programs/create`}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <PlusIcon className="h-4 w-4" />
              Create First Program
            </Link>
          </div>
        )}

        {/* Empty filtered state */}
        {!loading && programs.length > 0 && filteredPrograms.length === 0 && (
          <div className="text-center py-12 text-zinc-400 text-sm">
            No {filterStatus} programs.{' '}
            <button onClick={() => setFilterStatus('all')} className="text-[#5a8a1f] hover:underline ml-1 font-semibold">
              Show all
            </button>
          </div>
        )}

        {/* Program list */}
        {!loading && filteredPrograms.length > 0 && (
          <div className="space-y-3">
            {filteredPrograms.map((p) => {
              const badge = STATUS_BADGES[p.status] ?? STATUS_BADGES.draft;
              const regClose = toDate(p.registrationClose);
              const divCount = p.divisions?.length ?? 0;
              const regCount = regCounts[p.id];

              return (
                <Link
                  key={p.id}
                  to={`/clubs/${clubId}/programs/${p.id}`}
                  className="group block rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] hover:border-[#b5f03d]/40 hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-4">
                    {/* Lime medallion */}
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                      <RectangleGroupIcon className="h-5 w-5 text-[#5a8a1f]" />
                    </div>

                    {/* Left: info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <h3
                          className="text-base font-bold uppercase tracking-tight text-[#0d1f14] truncate group-hover:text-[#5a8a1f] transition-colors"
                          style={{ fontFamily: HEAD_FONT }}
                        >
                          {p.name}
                        </h3>
                        <span
                          className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full border ${badge.cls}`}
                          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                        >
                          {badge.label}
                        </span>
                      </div>

                      <p className="text-sm text-zinc-500 mt-0.5">
                        {[p.sport, divCount ? `${divCount} division${divCount !== 1 ? 's' : ''}` : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>

                      {p.description && (
                        <p className="text-sm text-zinc-400 mt-1 line-clamp-1">{p.description}</p>
                      )}

                      {regClose && (
                        <p className="text-xs text-zinc-400 mt-2">
                          Registration closes {format(regClose, 'MMM d, yyyy')}
                        </p>
                      )}
                    </div>

                    {/* Right: fee + count */}
                    <div className="text-right shrink-0">
                      <p className="text-xl font-bold text-[#0d1f14]" style={{ fontFamily: HEAD_FONT }}>
                        {formatCents(p.defaultFee)}
                      </p>
                      <p className="text-xs text-zinc-400">per player</p>
                      {p.earlyBirdFee > 0 && (
                        <p className="text-xs text-emerald-600 mt-0.5">
                          {formatCents(p.earlyBirdFee)} early bird
                        </p>
                      )}
                      {regCount != null && (
                        <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-[#b5f03d]/15 border border-[#b5f03d]/40">
                          <UserGroupIcon className="h-3.5 w-3.5 text-[#5a8a1f]" />
                          <span className="text-xs font-bold text-[#3d6010]" style={{ fontFamily: HEAD_FONT }}>
                            {regCount}
                          </span>
                          <span className="text-[10px] text-[#5a8a1f] uppercase font-semibold" style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}>
                            reg
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
